import { useMutation, type PureQueryOptions } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { graphql } from '~/apollo/generated/v4';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { SOFormFields } from '~/components/upload/supporting-object/so-form-fields';
import type { SOParent } from '~/components/upload/supporting-object/so-manager';
import { useZodForm } from '~/utils/forms';
import {
  initialProduction,
  productionSchema,
} from '~/utils/modules/supporting-object';

const CREATE_PRODUCTION = graphql(`
  mutation CreateProduction($input: ProductionCreateInput!) {
    productionCreate(input: $input) {
      result {
        id
      }
    }
  }
`);

type Props = SOParent & {
  refetchQueries?: PureQueryOptions[];
};

export function ProductionCreateForm({
  outcropId,
  studyId,
  refetchQueries,
}: Props) {
  const [createProduction, { loading, error }] = useMutation(
    CREATE_PRODUCTION,
    { refetchQueries },
  );

  const form = useZodForm({
    schema: productionSchema,
    values: initialProduction(),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await createProduction({
        variables: {
          input: {
            ...values,
            outcropId,
            studyId,
          },
        },
      });
      toast.success('Production created successfully.');
      form.reset();
    } catch (err) {
      console.log('Error creating production', err);
      toast.error('There was a problem creating the production.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <SOFormFields soType="PRODUCTION" studyId={studyId} />
        <HookFormErrors graphQLError={error} />

        <div className="card-actions justify-end">
          <Button type="submit" loading={loading} color="primary">
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
